import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /** Accessible name — required, since an icon-only button has no visible text
   * (WCAG 4.1.2). Also used as the tooltip. */
  label: string;
  children: ReactNode;
  /** Touch-target floor. Default 'md' = 44px (Apple HIG / Material 48dp region).
   * 'sm' = 24px (WCAG 2.5.8 minimum) for dense inline affordances where a 44px
   * box would break the surrounding text/table layout. */
  size?: 'sm' | 'md';
}

/**
 * Icon-only button with a guaranteed minimum touch target, a real aria-label,
 * and (via the global :focus-visible rule) a visible keyboard-focus ring. The
 * visible glyph can be any size; the min-h/min-w + centered flex give it the
 * full hit area. Replaces the many bespoke 16×16 icon buttons and emoji/×
 * controls the audit flagged (Apple HIG 44pt, Material 48dp, WCAG 2.5.8).
 */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, children, size = 'md', className = '', type = 'button', ...rest },
  ref,
) {
  const dim = size === 'sm' ? 'min-h-[24px] min-w-[24px]' : 'min-h-[44px] min-w-[44px]';
  return (
    <button
      ref={ref}
      // eslint-disable-next-line react/button-has-type
      type={type}
      aria-label={label}
      title={label}
      className={
        `inline-flex items-center justify-center ${dim} rounded-tremor-default ` +
        'text-tremor-content hover:text-tremor-content-strong hover:bg-tremor-background-muted ' +
        'transition-colors ' +
        className
      }
      {...rest}
    >
      {children}
    </button>
  );
});

export default IconButton;
